import { Invoice, PaymentProvider } from '@/hooks/invoice/types';
import { InvoiceContext } from '@/hooks/invoice/useInvoice';
import { FetchError, useApi } from '@/hooks/useApi';
import { useConfig } from '@/hooks/useConfig';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const bankTransferPaymentId = 9999;

const normalizeServiceProvider = (serviceProvider: string): PaymentProvider => {
  switch (serviceProvider?.toLowerCase()) {
    case 'viatel_test_card':
      return 'inveroak';
    case 'viatel_test_swish':
      return 'swish';
    default:
      return serviceProvider?.toLowerCase() as PaymentProvider;
  }
};

export type InvoiceContextType = {
  invoice: Invoice | undefined;
  setInvoiceReferenceCode: (reference: string | undefined) => void;
  isLoading: boolean;
  refetchInvoice: () => Promise<unknown>;
  queryError: FetchError | null;
  bankTransferPaymentId: number;
  postInvoice: UseMutationResult<string, FetchError, any, unknown>;
  resetInvoice: () => Promise<unknown>;
  minimumPaymentAmount: number;
};

export const InvoiceProvider = ({ children }: { children: React.ReactNode }) => {
  const { urls } = useConfig();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const apiFetch = useApi();
  const [invoiceReferenceCode, setInvoiceReferenceCode] = useState<string | undefined>(undefined);

  const {
    isLoading,
    data: invoice,
    refetch: refetchInvoice,
    error: queryError
  } = useQuery<Invoice, FetchError>({
    queryKey: ['getInvoice', invoiceReferenceCode, urls.akovaPay],
    queryFn: async () => {
      const response = await apiFetch<Invoice>(`${urls.akovaPay}/v1/Invoice/${invoiceReferenceCode}`);

      const invoice = {
        ...response,
        paymentMethods: [
          ...response.paymentMethods.map((method) => ({
            ...method,
            serviceProvider: normalizeServiceProvider(method.serviceProvider)
          })),
          {
            paymentMethodId: bankTransferPaymentId,
            methodText: t('components.payment-option-selector.bankgiro'),
            methodIcon: 'bankgiro',
            methodCode: 'bankgiro',
            serviceProvider: 'banktransfer',
            minimumPaymentAmount: 100
          }
        ]
      } as Invoice;

      return invoice;
    },
    enabled: !!invoiceReferenceCode,
    retry: false
  });

  const postInvoice = useMutation<string, FetchError, any, unknown>({
    mutationFn: () => {
      return apiFetch<string>(`${urls.akovaPay}/v1/invoice`, {
        method: 'POST',
        body: null
      });
    }
  });

  const handleSetInvoiceReferenceCode = (reference: string | undefined) => {
    setInvoiceReferenceCode(reference);
  };

  const handleResetInvoice = async () => {
    setInvoiceReferenceCode(undefined);
    queryClient.setQueryData(['getInvoice', invoiceReferenceCode, urls.akovaPay], undefined);
  };

  return (
    <InvoiceContext.Provider
      value={{
        invoice,
        resetInvoice: handleResetInvoice,
        refetchInvoice,
        setInvoiceReferenceCode: handleSetInvoiceReferenceCode,
        bankTransferPaymentId,
        isLoading,
        queryError,
        postInvoice,
        minimumPaymentAmount: 100
      }}
    >
      {children}
    </InvoiceContext.Provider>
  );
};
